import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldStarIcon as ShieldStar, CookieIcon as Cookie, ScalesIcon as Scales, LockIcon as Lock, EnvelopeSimpleIcon as EnvelopeSimple, CrownIcon as Crown, DownloadSimpleIcon as DownloadSimple, ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API as API_ROOT } from "@/lib/api";
import { formatApiError, formatDate } from "@/lib/utils-app";

/**
 * Admin Compliance — the single audit surface for every consent the
 * platform captures (cookies, Terms, Privacy, marketing email, Buyer
 * Pass), plus the Pawprints community guidelines flag and the per-
 * listing buyer-checklist acks.
 *
 * Operations: spot-check daily volume and confirm the recorder is alive.
 * Legal:      look up "did this user accept Terms vX on date Y?".
 * Compliance: prove document-version coverage to regulators.
 * Audit:      one-click CSV export to send to outside counsel / insurer.
 */
const KIND_LABEL = {
  cookies: "Cookies",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  marketing_emails: "Marketing emails",
  buyer_pass: "Buyer Pass",
};
const KIND_ICON = {
  cookies: Cookie,
  terms: Scales,
  privacy: Lock,
  marketing_emails: EnvelopeSimple,
  buyer_pass: Crown,
};

export default function AdminCompliance() {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tab, setTab] = useState("consents"); // "consents" | "buyer" | "community"
  const [filter, setFilter] = useState({ kind: "any", email: "", since: "", until: "" });
  const [page, setPage] = useState({ items: [], total: 0, offset: 0 });
  const [listLoading, setListLoading] = useState(false);

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await api.get("/admin/compliance/summary");
      setSummary(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load compliance summary."));
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadList = async (resetOffset = false) => {
    setListLoading(true);
    const off = resetOffset ? 0 : page.offset;
    try {
      let url;
      const params = new URLSearchParams();
      params.set("limit", "50");
      params.set("offset", String(off));
      if (filter.email) params.set("email", filter.email);
      if (tab === "consents") {
        if (filter.kind && filter.kind !== "any") params.set("kind", filter.kind);
        if (filter.since) params.set("since", new Date(filter.since).toISOString());
        if (filter.until) params.set("until", new Date(filter.until).toISOString());
        url = `/admin/compliance/consents?${params.toString()}`;
      } else if (tab === "buyer") {
        url = `/admin/compliance/buyer-consents?${params.toString()}`;
      } else {
        url = `/admin/compliance/community-guidelines?${params.toString()}`;
      }
      const r = await api.get(url);
      setPage({ items: r.data.items || [], total: r.data.total || 0, offset: off });
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load records."));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  // Reset paging when tab changes; reload list whenever tab/filter changes.
  useEffect(() => {
    setPage((p) => ({ ...p, offset: 0 }));
    loadList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filter.kind && filter.kind !== "any") params.set("kind", filter.kind);
    if (filter.since) params.set("since", new Date(filter.since).toISOString());
    if (filter.until) params.set("until", new Date(filter.until).toISOString());
    const token = localStorage.getItem("token");
    // Open in a new tab so the browser handles the download dialog.
    // The endpoint requires Authorization header, so we can't use a
    // plain anchor — fetch + blob instead.
    fetch(`${API_ROOT}/admin/compliance/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Export failed (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `consents-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(e.message || "Export failed."));
  };

  const byKindBars = useMemo(() => {
    if (!summary) return [];
    const max = Math.max(1, ...Object.values(summary.by_kind || {}));
    return Object.entries(summary.by_kind || {}).map(([k, c]) => ({
      kind: k,
      count: c,
      pct: Math.round((c / max) * 100),
    }));
  }, [summary]);

  return (
    <AppShell>
      <div className="space-y-8" data-testid="admin-compliance-page">
        <header className="flex items-end justify-between flex-wrap gap-4 pb-5 relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:bg-gold-hairline">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
              <ShieldStar size={14} weight="duotone" /> Admin · Compliance
            </div>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3">
              Consent &amp; audit ledger
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
              Every server-side record of what users agreed to and when —
              cookies, Terms, Privacy, marketing email, Buyer Pass, the
              Pawprints community guidelines, and per-listing buyer
              checklist acks. Export to CSV for legal or your insurer.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={exportCsv}
              className="rounded-full bg-ink text-white hover:bg-crimson h-10"
              data-testid="compliance-export-csv"
            >
              <DownloadSimple size={14} weight="bold" className="mr-1.5" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => { loadSummary(); loadList(true); }}
              className="rounded-full border-[#E5E5E5]"
              data-testid="compliance-refresh"
            >
              Refresh
            </Button>
          </div>
        </header>

        {/* Hero stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="compliance-hero-stats">
          <HeroStat
            label="Total consent events"
            value={summary?.total_events ?? "—"}
            sub={summary ? `${summary.events_last_24h} in last 24h` : ""}
            loading={summaryLoading}
            testId="stat-total"
          />
          <HeroStat
            label="Users with server consents"
            value={
              summary
                ? `${summary.users_with_server_consents} / ${summary.users_total}`
                : "—"
            }
            sub="One row per signed-in person."
            loading={summaryLoading}
            testId="stat-coverage"
          />
          <HeroStat
            label="Community guidelines"
            value={summary?.pawprints_guidelines_accepted ?? "—"}
            sub="Accepted to post on The Dog's Voice."
            loading={summaryLoading}
            testId="stat-pawprints"
          />
          <HeroStat
            label="Buyer-checklist acks"
            value={summary?.buyer_checklist_acknowledgements ?? "—"}
            sub="Per (user × listing) acknowledgements."
            loading={summaryLoading}
            testId="stat-buyer"
          />
        </section>

        {/* By kind + by document version */}
        <section className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5" data-testid="compliance-by-kind">
            <h2 className="font-spectral text-xl mb-4">By kind</h2>
            {byKindBars.length === 0 ? (
              <p className="text-sm text-[#6B6B6B]">No consent events recorded yet.</p>
            ) : (
              <ul className="space-y-3">
                {byKindBars.map((b) => {
                  const Icon = KIND_ICON[b.kind] || Scales;
                  return (
                    <li key={b.kind} className="space-y-1">
                      <div className="flex items-center justify-between text-[12.5px]">
                        <span className="flex items-center gap-1.5">
                          <Icon size={14} weight="duotone" className="text-gold" />
                          <strong>{KIND_LABEL[b.kind] || b.kind}</strong>
                        </span>
                        <span className="font-tabular text-ink">{b.count}</span>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-ink rounded-full"
                          style={{ width: `${b.pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5" data-testid="compliance-by-version">
            <h2 className="font-spectral text-xl mb-4">By document version (top 20)</h2>
            {(summary?.by_document_version || []).length === 0 ? (
              <p className="text-sm text-[#6B6B6B]">
                No versioned Terms or Privacy acceptances yet. Versions
                appear once consent rows carry a <code>document_version</code> string.
              </p>
            ) : (
              <ul className="text-[12.5px] divide-y divide-[#F0F0F0]">
                {(summary?.by_document_version || []).map((v, i) => (
                  <li key={i} className="py-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center px-1.5 h-4 text-[9.5px] font-bold rounded bg-ink text-white uppercase tracking-[0.1em]">
                        {v.kind}
                      </span>
                      <span className="font-tabular">{v.document_version}</span>
                    </span>
                    <span className="font-semibold">{v.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Tab nav */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="compliance-tabs">
          <TabBtn active={tab === "consents"} onClick={() => setTab("consents")} testId="tab-consents">
            Cookie · Terms · Privacy
          </TabBtn>
          <TabBtn active={tab === "buyer"} onClick={() => setTab("buyer")} testId="tab-buyer">
            Buyer checklist
          </TabBtn>
          <TabBtn active={tab === "community"} onClick={() => setTab("community")} testId="tab-community">
            Community guidelines
          </TabBtn>
        </div>

        {/* Filter bar */}
        {tab === "consents" && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-stone-50 border border-stone-200 rounded-2xl p-3" data-testid="compliance-filter-bar">
            <Select
              value={filter.kind}
              onValueChange={(v) => setFilter((f) => ({ ...f, kind: v }))}
            >
              <SelectTrigger className="h-9 text-[12.5px] bg-white" data-testid="filter-kind">
                <SelectValue placeholder="Any kind" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any kind</SelectItem>
                {Object.entries(KIND_LABEL).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Email contains…"
              value={filter.email}
              onChange={(e) => setFilter((f) => ({ ...f, email: e.target.value }))}
              className="h-9 text-[12.5px] bg-white"
              data-testid="filter-email"
            />
            <Input
              type="date"
              value={filter.since}
              onChange={(e) => setFilter((f) => ({ ...f, since: e.target.value }))}
              className="h-9 text-[12.5px] bg-white"
              data-testid="filter-since"
            />
            <Input
              type="date"
              value={filter.until}
              onChange={(e) => setFilter((f) => ({ ...f, until: e.target.value }))}
              className="h-9 text-[12.5px] bg-white"
              data-testid="filter-until"
            />
            <Button
              onClick={() => loadList(true)}
              disabled={listLoading}
              className="rounded-full h-9 bg-ink text-white text-[12px]"
              data-testid="filter-apply"
            >
              {listLoading ? "Searching…" : "Apply filter"}
            </Button>
          </div>
        )}
        {(tab === "buyer" || tab === "community") && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-stone-50 border border-stone-200 rounded-2xl p-3">
            <Input
              placeholder="Email contains…"
              value={filter.email}
              onChange={(e) => setFilter((f) => ({ ...f, email: e.target.value }))}
              className="h-9 text-[12.5px] bg-white md:col-span-4"
              data-testid="filter-email-simple"
            />
            <Button
              onClick={() => loadList(true)}
              disabled={listLoading}
              className="rounded-full h-9 bg-ink text-white text-[12px]"
              data-testid="filter-apply-simple"
            >
              {listLoading ? "Searching…" : "Apply filter"}
            </Button>
          </div>
        )}

        {/* List */}
        {tab === "consents" && <ConsentsTable rows={page.items} loading={listLoading} />}
        {tab === "buyer" && <BuyerConsentsTable rows={page.items} loading={listLoading} />}
        {tab === "community" && <CommunityGuidelinesTable rows={page.items} loading={listLoading} />}

        {/* Pagination */}
        {page.total > 50 && (
          <div className="flex items-center justify-between text-xs text-[#6B6B6B]" data-testid="compliance-pagination">
            <span>
              Showing {page.offset + 1}–{Math.min(page.offset + 50, page.total)} of {page.total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={page.offset === 0 || listLoading}
                onClick={() => {
                  setPage((p) => ({ ...p, offset: Math.max(0, p.offset - 50) }));
                  setTimeout(() => loadList(false), 0);
                }}
                className="h-8 text-xs rounded-full"
                data-testid="page-prev"
              >
                Prev
              </Button>
              <Button
                variant="outline"
                disabled={page.offset + 50 >= page.total || listLoading}
                onClick={() => {
                  setPage((p) => ({ ...p, offset: p.offset + 50 }));
                  setTimeout(() => loadList(false), 0);
                }}
                className="h-8 text-xs rounded-full"
                data-testid="page-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ---------- small bits -------------------------------------------------

function HeroStat({ label, value, sub, loading, testId }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-ink/55 font-bold">{label}</p>
      <p className="font-spectral text-3xl mt-1.5 font-tabular">
        {loading ? "…" : value}
      </p>
      <p className="text-[11px] text-[#6B6B6B] mt-1 leading-snug">{sub}</p>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-9 px-4 rounded-full text-[12px] font-bold uppercase tracking-[0.1em] transition-colors ${
        active
          ? "bg-ink text-white"
          : "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"
      }`}
    >
      {children}
    </button>
  );
}

function ConsentsTable({ rows, loading }) {
  if (loading) return <p className="text-sm text-[#6B6B6B]">Loading…</p>;
  if (!rows.length) {
    return (
      <div className="text-sm text-[#6B6B6B] bg-stone-50 border border-stone-200 rounded-2xl p-5" data-testid="consents-empty">
        No consent events match this filter.
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl overflow-hidden" data-testid="consents-table">
      <table className="w-full text-[12.5px]">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-[0.1em] text-ink/55">
          <tr>
            <th className="text-left p-3">When</th>
            <th className="text-left p-3">Kind</th>
            <th className="text-left p-3">Value</th>
            <th className="text-left p-3">Doc version</th>
            <th className="text-left p-3">User</th>
            <th className="text-left p-3">IP · UA</th>
            <th className="text-left p-3">Origin</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0F0]">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`consent-row-${r.id}`}>
              <td className="p-3 font-tabular whitespace-nowrap">{formatDate(r.created_at, true)}</td>
              <td className="p-3">
                <span className="inline-flex items-center px-1.5 h-5 text-[10px] font-bold rounded bg-ink text-white uppercase tracking-[0.1em]">
                  {r.kind}
                </span>
              </td>
              <td className="p-3 font-semibold">{r.value}</td>
              <td className="p-3 text-[#6B6B6B] font-tabular">{r.document_version || "—"}</td>
              <td className="p-3 max-w-[200px]">
                {r.user_id ? (
                  <Link
                    to={`/admin/users?u=${r.user_id}`}
                    className="text-ink hover:text-crimson"
                  >
                    <div className="truncate font-semibold">{r.user_email || "(no email)"}</div>
                    <div className="text-[10.5px] text-[#6B6B6B] truncate">{r.user_name || ""}</div>
                  </Link>
                ) : (
                  <span className="text-[#6B6B6B] italic">anon · {r.anon_id?.slice(0, 8) || "—"}</span>
                )}
              </td>
              <td className="p-3 max-w-[220px] text-[11px] text-[#6B6B6B]">
                <div className="truncate">{r.ip || "—"}</div>
                <div className="truncate">{r.user_agent || "—"}</div>
              </td>
              <td className="p-3 text-[11px]">{r.origin || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BuyerConsentsTable({ rows, loading }) {
  if (loading) return <p className="text-sm text-[#6B6B6B]">Loading…</p>;
  if (!rows.length) {
    return (
      <div className="text-sm text-[#6B6B6B] bg-stone-50 border border-stone-200 rounded-2xl p-5">
        No buyer-checklist acknowledgements match this filter.
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl overflow-hidden">
      <table className="w-full text-[12.5px]" data-testid="buyer-consents-table">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-[0.1em] text-ink/55">
          <tr>
            <th className="text-left p-3">When</th>
            <th className="text-left p-3">Buyer</th>
            <th className="text-left p-3">Listing</th>
            <th className="text-left p-3">Breed shown</th>
            <th className="text-left p-3">Warning copy</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0F0]">
          {rows.map((r, i) => (
            <tr key={`${r.user_id}-${r.dog_id}-${i}`}>
              <td className="p-3 font-tabular whitespace-nowrap">{formatDate(r.acknowledged_at, true)}</td>
              <td className="p-3">
                <div className="truncate font-semibold">{r.user_email || r.user_id}</div>
                <div className="text-[10.5px] text-[#6B6B6B] truncate">{r.user_name || ""}</div>
              </td>
              <td className="p-3">
                <Link
                  to={`/dogs/${r.dog_id}`}
                  className="inline-flex items-center gap-1 text-ink hover:text-crimson"
                >
                  {r.dog_name || r.dog_id.slice(0, 8)}
                  <ArrowSquareOut size={11} weight="bold" />
                </Link>
              </td>
              <td className="p-3 text-[#6B6B6B]">{r.breed || r.dog_breed_actual || "—"}</td>
              <td className="p-3 text-[11px] text-[#6B6B6B] truncate max-w-[300px]">
                {r.breed_warning || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommunityGuidelinesTable({ rows, loading }) {
  if (loading) return <p className="text-sm text-[#6B6B6B]">Loading…</p>;
  if (!rows.length) {
    return (
      <div className="text-sm text-[#6B6B6B] bg-stone-50 border border-stone-200 rounded-2xl p-5">
        No users have accepted the community guidelines yet.
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl overflow-hidden">
      <table className="w-full text-[12.5px]" data-testid="community-guidelines-table">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-[0.1em] text-ink/55">
          <tr>
            <th className="text-left p-3">Accepted at</th>
            <th className="text-left p-3">User</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0F0]">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="p-3 font-tabular whitespace-nowrap">{formatDate(r.pawprints_terms_accepted_at, true)}</td>
              <td className="p-3">
                <Link
                  to={`/admin/users?u=${r.id}`}
                  className="text-ink hover:text-crimson"
                >
                  <div className="truncate font-semibold">{r.email}</div>
                  <div className="text-[10.5px] text-[#6B6B6B] truncate">{r.name || ""}</div>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
